import { Dialog, Grid, StandardTextFieldProps } from '@material-ui/core';
import Warning from '@material-ui/icons/Warning';
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import BaseButton from 'src/common/components/base-button';
import { validateAlternateIdOrPhoneLength } from 'src/common/helpers/validation-helper';
import { styles } from './customer-loyalty-search-styles';
import { TextInput } from '@ncr-retail/base-retail-components';
import TenderGroupTitle from 'src/tendering/components/tender-group-title';
import clsx from 'clsx';
import { ScannedDataHandler, updateScannerCallback } from 'src/devices/scanner-operations';
import { validateStringIsOnlyLetters } from 'src/common/helpers/text-formatting-helper';
import { ICustomerSearchCriteria } from 'src/common/interfaces/dtos/ICustomerSearchCriteria';
import { useTranslation } from 'react-i18next';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import DoubleTrainingModeWatermark from 'src/common/training-mode/double-training-mode-watermark';

const defaultInputValues: StandardTextFieldProps = {
  value: '',
  error: false,
  helperText: '',
};

export const CustomerLoyaltySearchForm = (props: CustomerLoyaltySearchProps): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  const altIdRef = useRef<HTMLInputElement>(null);
  const [fieldsIncomplete, setFieldsIncomplete] = useState(false);
  const [altIdPhoneInputElement, setAltIdPhoneInputElement] = useState<StandardTextFieldProps>(
    defaultInputValues,
  );
  const [firstNameInputElement, setFirstNameInputElement] = useState<StandardTextFieldProps>(
    defaultInputValues,
  );
  const [lastNameInputElement, setLastNameInputElement] = useState<StandardTextFieldProps>(
    defaultInputValues,
  );
  const {
    showPhoneNumberOnCustomerSearch,
    validateLettersOnlyOnSearchForm,
  } = getCurrentXLRConfig();

  const setStateAction = (
    inputFieldsProperties: React.Dispatch<React.SetStateAction<StandardTextFieldProps>>[],
  ): void => {
    inputFieldsProperties.forEach(inputFieldProperty => {
      inputFieldProperty({ ...defaultInputValues });
    });
  };

  const onTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { id, value } = event.target;
    switch (id) {
      case 'alternateIdOrPhone':
        setAlternateIdOrPhoneLengthAndError(value);
        setStateAction([setFirstNameInputElement, setLastNameInputElement]);
        break;
      case 'first-name':
        setFirstNameInputElement({ ...defaultInputValues, value: value });
        if (validateLettersOnlyOnSearchForm && !validateStringIsOnlyLetters(value)) {
          setFirstNameInputElement({
            ...firstNameInputElement,
            error: true,
            helperText: t('CustomerDetailsTextOnlyValidator'),
          });
        }
        setStateAction([setAltIdPhoneInputElement]);
        break;
      case 'last-name':
        setLastNameInputElement({ ...defaultInputValues, value: value });
        if (validateLettersOnlyOnSearchForm && !validateStringIsOnlyLetters(value)) {
          setLastNameInputElement({
            ...lastNameInputElement,
            error: true,
            helperText: t('CustomerDetailsTextOnlyValidator'),
          });
        }
        setStateAction([setAltIdPhoneInputElement]);
        break;
    }
  };

  const onSearch = (): void => {
    const criteria = buildCriteria(
      (firstNameInputElement.value as string).trim(),
      (lastNameInputElement.value as string).trim(),
      (altIdPhoneInputElement.value as string).trim(),
    );
    props.onSearch(criteria);
  };

  const setAlternateIdOrPhoneLengthAndError = useCallback(
    (value: string): void => {
      validateAlternateIdOrPhoneLength(value)
        .then(() => {
          setAltIdPhoneInputElement({ ...defaultInputValues, value: value });
        })
        .catch((reason): void => {
          setAltIdPhoneInputElement(state => ({
            ...state,
            value: value,
            error: true,
            helperText: t(reason.t, reason.args),
          }));
        });
    },
    [t],
  );

  const onDataScanned: ScannedDataHandler = useCallback(
    async (scanData: string): Promise<void> => {
      setAlternateIdOrPhoneLengthAndError(scanData);
      setFirstNameInputElement({ ...defaultInputValues });
      setLastNameInputElement({ ...defaultInputValues });
    },
    [setAlternateIdOrPhoneLengthAndError],
  );

  useEffect(() => {
    updateScannerCallback(onDataScanned);
  }, [onDataScanned]);

  const inputIcon = (isError: boolean): { icon: JSX.Element } | undefined =>
    isError ? { icon: <Warning /> } : undefined;

  const hasInvalidFields = useCallback((): boolean => {
    const altValue = altIdPhoneInputElement.value as string;
    const firstNameValue = firstNameInputElement.value as string;
    const lastNameValue = lastNameInputElement.value as string;
    if (altValue.length) {
      return altIdPhoneInputElement.error === true;
    }
    return !firstNameValue || !lastNameValue;
  }, [altIdPhoneInputElement, firstNameInputElement, lastNameInputElement]);

  useEffect(() => {
    setFieldsIncomplete(hasInvalidFields());
  }, [altIdPhoneInputElement, hasInvalidFields]);

  return (
    <Dialog
      open={true}
      classes={{
        paper: props.trainingModeActive ? classes.trainingModeBackground : classes.dialogPaper,
      }}
    >
      {props.trainingModeActive && <DoubleTrainingModeWatermark />}
      <Grid container direction='column' className={classes.contentContainer}>
        <TenderGroupTitle title={t('CustomerFormSearchTitle')} />
      </Grid>
      <Grid
        container
        justifyContent='center'
        alignItems='center'
        direction='row'
        className={`${classes.contentContainer} ${classes.form}`}
      >
        <Grid item className={classes.formFieldsWrapper}>
          <Grid container direction='column'>
            <TextInput
              type='text'
              helperText={firstNameInputElement.helperText}
              value={firstNameInputElement.value}
              error={!!firstNameInputElement.helperText}
              onChange={onTextChange}
              autoComplete='off'
              id='first-name'
              label={t('CustomerDetailsForReturnView_FirstName')}
              name='first-name'
              inputIcon={inputIcon(!!firstNameInputElement.helperText)}
              className={clsx('formInputHeight', {
                [classes.looksDisabled]: !!altIdPhoneInputElement.value,
              })}
              onClick={() => setStateAction([setAltIdPhoneInputElement])}
              inputProps={{
                'data-testid': 'search-first-name',
                'data-first-name-looks-disabled': !!altIdPhoneInputElement.value,
                className: 'global-keyboard',
              }}
              FormHelperTextProps={{
                className: classes.helper,
              }}
            />
            <TextInput
              type='text'
              helperText={lastNameInputElement.helperText}
              value={lastNameInputElement.value}
              error={!!lastNameInputElement.helperText}
              onChange={onTextChange}
              autoComplete='off'
              id='last-name'
              label={t('CustomerDetailsForReturnView_LastName')}
              name='last-name'
              inputIcon={inputIcon(!!lastNameInputElement.helperText)}
              className={clsx('formInputHeight', {
                [classes.looksDisabled]: !!altIdPhoneInputElement.value,
              })}
              onClick={() => setStateAction([setAltIdPhoneInputElement])}
              inputProps={{
                'data-testid': 'search-last-name',
                'data-last-name-looks-disabled': !!altIdPhoneInputElement.value,
                className: 'global-keyboard',
              }}
              FormHelperTextProps={{
                className: classes.helper,
              }}
            />
          </Grid>
        </Grid>
        <Grid item className={classes.formCenterOr}>
          {t('CustomerLoyaltyFormOr')}
        </Grid>
        <Grid item className={classes.formFieldsWrapper}>
          <Grid container direction='column'>
            <TextInput
              label={
                showPhoneNumberOnCustomerSearch
                  ? t('CustomerDetailsForReturnView_HomeOrMobile')
                  : t('CustomerDetailsAlternativeId_Label')
              }
              inputRef={altIdRef}
              name='alternateIdOrPhone'
              type='text'
              helperText={altIdPhoneInputElement.helperText}
              value={altIdPhoneInputElement.value}
              error={!!altIdPhoneInputElement.helperText}
              onChange={onTextChange}
              autoComplete='off'
              id='alternateIdOrPhone'
              inputIcon={inputIcon(!!altIdPhoneInputElement.helperText)}
              className={clsx('formInputHeight', {
                [classes.looksDisabled]:
                  !!firstNameInputElement.value || !!lastNameInputElement.value,
              })}
              onClick={() => setStateAction([setFirstNameInputElement, setLastNameInputElement])}
              inputProps={{
                'data-testid': 'search-altId',
                'data-altid-looks-disabled':
                  !!firstNameInputElement.value || !!lastNameInputElement.value,
                className: 'global-keyboard',
              }}
              FormHelperTextProps={{
                className: classes.helper,
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        container
        justifyContent='center'
        className={`${classes.contentContainer} ${classes.gap10}`}
      >
        <Grid item>
          <BaseButton
            buttonType='white'
            onClick={props.onClose}
            data-testid='customer-search-button-close'
            className={classes.closeButton}
          >
            {t('CustomerLoyaltyFormCancel')}
          </BaseButton>
        </Grid>

        <Grid item>
          <BaseButton
            onClick={onSearch}
            data-testid='customer-search-btn'
            disabled={fieldsIncomplete}
            className={classes.searchButton}
          >
            {t('CustomerLoyaltyFormSearch')}
          </BaseButton>
        </Grid>
      </Grid>
    </Dialog>
  );
};

interface CustomerLoyaltySearchProps {
  onClose: () => void;
  trainingModeActive: boolean;
  onSearch: (criteria: Partial<ICustomerSearchCriteria>) => void;
}

const buildCriteria = (
  firstName: string,
  lastName: string,
  alternateId: string,
): Partial<ICustomerSearchCriteria> => {
  let criteria: Partial<ICustomerSearchCriteria> = {
    alternateId,
  };
  if (firstName && lastName) {
    criteria = {
      firstName,
      lastName,
    };
  }
  return criteria;
};
